import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, TextField, Button, Link as MuiLink, Alert, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';

const PageTitle = styled(Typography)({
  fontFamily: 'Cormorant Garamond, serif',
  fontSize: '2.5rem',
  color: '#1a1a1a',
  textAlign: 'center',
  marginBottom: '2rem',
});

const StyledForm = styled('form')({
  display: 'flex',
  flexDirection: 'column',
  gap: '1.5rem',
  maxWidth: '400px',
  margin: '0 auto',
});

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    '& fieldset': {
      borderColor: '#1a1a1a',
    },
    '&:hover fieldset': {
      borderColor: '#c4a661',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#c4a661',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#1a1a1a',
    '&.Mui-focused': {
      color: '#c4a661',
    },
  },
});

const SubmitButton = styled(Button)({
  backgroundColor: '#1a1a1a',
  color: '#fff',
  padding: '1rem',
  '&:hover': {
    backgroundColor: '#c4a661',
  },
  '&.Mui-disabled': {
    backgroundColor: '#ccc',
    color: '#fff',
  },
});

const StyledLink = styled(MuiLink)({
  color: '#1a1a1a',
  textDecoration: 'none',
  '&:hover': {
    color: '#c4a661',
  },
});

const LoadingWrapper = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '1rem',
});

const initialFormState = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  confirmPassword: '',
};

const Register = () => {
  const [formData, setFormData] = useState(initialFormState);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signup, user } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/profile');
    }
  }, [user, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const validateForm = () => {
    // Name validation
    if (!formData.firstName.trim()) {
      setError('First name is required');
      return false;
    }
    if (!formData.lastName.trim()) {
      setError('Last name is required');
      return false;
    }

    // Email validation with regex
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!formData.email.trim() || !emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }

    // Password validation
    if (!formData.password) {
      setError('Password is required');
      return false;
    }

    // Enhanced password requirements
    const passwordChecks = {
      length: formData.password.length >= 8,
      uppercase: /[A-Z]/.test(formData.password),
      lowercase: /[a-z]/.test(formData.password),
      number: /\d/.test(formData.password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(formData.password)
    };

    if (!passwordChecks.length) {
      setError('Password must be at least 8 characters long');
      return false;
    }
    if (!passwordChecks.uppercase || !passwordChecks.lowercase) {
      setError('Password must contain both uppercase and lowercase letters');
      return false;
    }
    if (!passwordChecks.number) {
      setError('Password must contain at least one number');
      return false;
    }
    if (!passwordChecks.special) {
      setError('Password must contain at least one special character');
      return false;
    }

    // Password confirmation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    // Check for common passwords
    const commonPasswords = ['Password123!', 'Admin123!', 'Welcome123!', 'Letmein123!'];
    if (commonPasswords.includes(formData.password)) {
      setError('Please use a less common password');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (!validateForm()) return;

    setError('');
    setLoading(true);

    try {
      await signup(
        formData.email,
        formData.password,
        formData.firstName,
        formData.lastName
      );
      setFormData(initialFormState);
      navigate('/profile');
    } catch (err) {
      let errorMessage = 'Failed to create account';
      
      // Handle specific Firebase Auth errors
      switch (err.code) {
        case 'auth/email-already-in-use':
          errorMessage = 'An account with this email already exists';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Invalid email address';
          break;
        case 'auth/operation-not-allowed':
          errorMessage = 'Email/password accounts are not enabled. Please contact support.';
          break;
        case 'auth/weak-password':
          errorMessage = 'Password is too weak';
          break;
        default:
          errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Box sx={{ py: 8 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <PageTitle>Create Account</PageTitle>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          <StyledForm onSubmit={handleSubmit} noValidate>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <StyledTextField
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
                fullWidth
                autoComplete="given-name"
                disabled={loading}
                error={error === 'First name is required'}
              />
              <StyledTextField
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
                fullWidth
                autoComplete="family-name"
                disabled={loading}
                error={error === 'Last name is required'}
              />
            </Box>
            <StyledTextField
              label="Email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              fullWidth
              autoComplete="email"
              inputProps={{
                autoCapitalize: 'none'
              }}
              disabled={loading}
              error={error === 'Email is required' || error === 'Invalid email address'}
            />
            <StyledTextField
              label="Password"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              fullWidth
              autoComplete="new-password"
              disabled={loading}
              error={error === 'Password is required' || error === 'Password must be at least 6 characters long'}
              helperText="Password must be at least 8 characters long and contain uppercase, lowercase, number, and special character"
            />
            <StyledTextField
              label="Confirm Password"
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              fullWidth
              autoComplete="new-password"
              disabled={loading}
              error={error === 'Passwords do not match'}
            />
            <SubmitButton 
              type="submit" 
              variant="contained" 
              fullWidth
              disabled={loading}
            >
              {loading ? (
                <LoadingWrapper>
                  <CircularProgress size={20} color="inherit" />
                  <span>Creating Account...</span>
                </LoadingWrapper>
              ) : (
                'Create Account'
              )}
            </SubmitButton>
            <Typography textAlign="center" mt={2}>
              Already have an account?{' '}
              <StyledLink component={Link} to="/login">
                Sign in
              </StyledLink>
            </Typography>
          </StyledForm>
        </motion.div>
      </Box>
    </Container>
  );
};

export default Register;
